import React, { Component } from "react";
import { withStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";

import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import DirectionsBoatIcon from "@material-ui/icons/DirectionsBoat";
import SearchIcon from "@material-ui/icons/Search";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CheckIcon from "@material-ui/icons/Check";

import utils from "../../universal/utils";

const LS_KEY = "sportyshop_pending_on_demand_orders";
const basePath = utils.getEdgeBasePath();

// Shared status catalog – mirrors edge/constants/onDemandStatuses.js
const ON_DEMAND_STATUSES = [
  { key: "received", title: "ההזמנה התקבלה", explanation: "קיבלנו את ההזמנה שלך והיא ממתינה לאישור תשלום והמשך טיפול.", order: 1 },
  { key: "paid", title: "התשלום אושר", explanation: "התשלום עבור ההזמנה התקבל בהצלחה והתחלנו לטפל בהזמנת הצמיגים מהמלאי האירופי.", order: 2 },
  { key: "left_supplier_to_logistics_center", title: "הצמיגים יצאו מהספק", explanation: "הצמיגים יצאו מהספק בדרכם למרכז הלוגיסטי שלנו באירופה.", order: 3 },
  { key: "arrived_logistics_center", title: "הצמיגים הגיעו למרכז הלוגיסטי", explanation: "הצמיגים הגיעו למרכז הלוגיסטי שלנו וממתינים להמשך תהליך השילוח.", order: 4 },
  { key: "in_eu_customs", title: "הצמיגים במכס באירופה", explanation: "הצמיגים נמצאים בתהליך מכס באירופה לפני העלייה למשלוח לישראל.", order: 5 },
  { key: "container_enroute_to_israel", title: "המכולה בדרך לישראל", explanation: "המכולה עם הצמיגים יצאה מאירופה והיא בדרכה לישראל.", order: 6 },
  { key: "arrived_israel_pending_customs", title: "המכולה הגיעה לישראל", explanation: "המכולה הגיעה לישראל וממתינה לשחרור מהמכס.", order: 7 },
  { key: "container_released", title: "המכולה שוחררה", explanation: "המכולה שוחררה מהמכס והצמיגים עוברים להמשך טיפול מקומי.", order: 8 },
  { key: "tires_shipped", title: "הצמיגים נשלחו", explanation: "הצמיגים נשלחו ליעד שנבחר בהזמנה או למוסך ההתקנה.", order: 9 },
  { key: "order_complete", title: "ההזמנה הושלמה", explanation: "ההזמנה טופלה במלואה. תודה שקנית בספורטישופ.", order: 10, terminal: true },
  { key: "cancelled", title: "ההזמנה בוטלה", explanation: "הטיפול בהזמנה הופסק. אם יש לך שאלות אפשר ליצור איתנו קשר.", order: 11, terminal: true },
];

const TIMELINE_STATUSES = ON_DEMAND_STATUSES.filter((s) => !s.terminal);
const STATUS_MAP = Object.fromEntries(ON_DEMAND_STATUSES.map((s) => [s.key, s]));

function getLocalOrders() {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || "[]");
  } catch (_) {
    return [];
  }
}

function saveLocalOrders(orders) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(orders));
  } catch (_) {}
}

function removeLocalOrder(orderId) {
  const orders = getLocalOrders().filter((o) => String(o.orderId) !== String(orderId));
  saveLocalOrders(orders);
}

function formatTimeSince(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const elapsedMs = Date.now() - date.getTime();
  const elapsedSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
  const elapsedMinutes = Math.floor(elapsedSeconds / 60);
  const elapsedHours = Math.floor(elapsedMinutes / 60);
  const elapsedDays = Math.floor(elapsedHours / 24);
  const elapsedMonths = Math.floor(elapsedDays / 30);
  const elapsedYears = Math.floor(elapsedDays / 365);

  if (elapsedSeconds < 45) return "עכשיו";
  if (elapsedMinutes < 2) return "לפני דקה";
  if (elapsedMinutes < 60) return `לפני ${elapsedMinutes} דקות`;
  if (elapsedHours < 2) return "לפני שעה";
  if (elapsedHours < 24) return `לפני ${elapsedHours} שעות`;
  if (elapsedDays < 2) return "לפני יום";
  if (elapsedDays < 30) return `לפני ${elapsedDays} ימים`;
  if (elapsedMonths < 2) return "לפני חודש";
  if (elapsedMonths < 12) return `לפני ${elapsedMonths} חודשים`;
  if (elapsedYears < 2) return "לפני שנה";
  return `לפני ${elapsedYears} שנים`;
}

const styles = (theme) => ({
  root: {
    direction: "rtl",
    maxWidth: 860,
    margin: "56px auto 32px",
    padding: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1.5),
      margin: "56px auto 16px",
    },
  },
  header: {
    display: "flex",
    flexDirection: "row-reverse",
    alignItems: "center",
    gap: 12,
    marginBottom: theme.spacing(3),
    textAlign: "right",
    [theme.breakpoints.down("xs")]: {
      gap: 8,
      marginBottom: theme.spacing(2),
    },
  },
  headerTitle: {
    [theme.breakpoints.down("xs")]: {
      fontSize: "1.6rem",
    },
  },
  orderCard: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
  },
  orderTitle: {
    display: "flex",
    flexDirection: "row-reverse",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: theme.spacing(1),
    textAlign: "right",
  },
  orderTitleMain: {
    display: "flex",
    flexDirection: "row-reverse",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
    minWidth: 0,
  },
  orderTitleText: {
    whiteSpace: "nowrap",
  },
  orderTitleMeta: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },
  updateHistory: {
    marginTop: 16,
    padding: "10px 14px",
    background: "#f9fafb",
    borderRadius: 8,
    fontSize: 13,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    direction: "rtl",
    textAlign: "right",
  },
  updateEntry: {
    borderBottom: "1px solid #e5e7eb",
    paddingBottom: 7,
    marginBottom: 7,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    direction: "rtl",
    textAlign: "right",
    unicodeBidi: "plaintext",
    "&:last-child": { borderBottom: "none", marginBottom: 0 },
  },
  lookupPanel: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    border: "1px dashed #d1d5db",
    borderRadius: 12,
    background: "#fafafa",
    direction: "rtl",
    textAlign: "right",
  },
  lookupRow: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    alignItems: "flex-end",
    justifyContent: "flex-start",
    direction: "rtl",
    marginTop: theme.spacing(2),
  },
  lookupFieldId: {
    width: 160,
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
  lookupFieldDate: {
    width: 200,
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
  lookupButton: {
    "& .MuiButton-startIcon": {
      marginRight: 0,
      marginLeft: 8,
    },
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
  lookupField: {
    direction: "rtl",
    "& .MuiOutlinedInput-root": {
      direction: "rtl",
    },
    "& input": {
      textAlign: "right",
    },
    "& legend": {
      textAlign: "right",
    },
    "& label": {
      right: 14,
      left: "auto",
      direction: "rtl",
      transformOrigin: "top right",
      whiteSpace: "nowrap",
    },
    "& label.MuiInputLabel-outlined": {
      transform: "translate(0, 10px) scale(1)",
    },
    "& label.MuiInputLabel-shrink": {
      transform: "translate(0, -6px) scale(0.75)",
    },
  },
  chip: {
    fontSize: 12,
    height: 24,
  },
  emptyState: {
    textAlign: "center",
    padding: 60,
    color: "#6b7280",
  },
  itemsList: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 8,
    justifyContent: "flex-end",
  },
  orderItem: {
    display: "flex",
    flexDirection: "row-reverse",
    alignItems: "center",
    gap: 8,
    border: "1px solid #d1d5db",
    borderRadius: 18,
    padding: "4px 10px",
    background: "#ffffff",
  },
  orderItemImage: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    objectFit: "cover",
    border: "1px solid #e5e7eb",
    background: "#f9fafb",
  },
});

function statusColor(key) {
  if (key === "order_complete") return "#16a34a";
  if (key === "cancelled") return "#dc2626";
  if (key === "tires_shipped") return "#7c3aed";
  if (["received", "paid"].includes(key)) return "#2563eb";
  return "#d97706";
}

class PendingOrdersPage extends Component {
  state = {
    localOrders: [],
    serverOrders: {},
    loadingOrders: {},
    lookupOrderId: "",
    lookupOrderDate: "",
    lookupLoading: false,
    lookupError: null,
    lookupSuccess: null,
    removeConfirmOrderId: null,
  };

  componentDidMount() {
    const localOrders = getLocalOrders();
    this.setState({ localOrders });
    localOrders.forEach((lo) => this.fetchOrderFromServer(lo));
  }

  fetchOrderFromServer(localOrder) {
    const { orderId, customerAccessToken } = localOrder;
    this.setState((prev) => ({
      loadingOrders: { ...prev.loadingOrders, [orderId]: true },
    }));

    fetch(`${basePath}/api/v1/on-demand-orders/public/${orderId}/${customerAccessToken}`)
      .then((r) => r.json())
      .then((data) => {
        this.setState((prev) => ({
          loadingOrders: { ...prev.loadingOrders, [orderId]: false },
          serverOrders: { ...prev.serverOrders, [orderId]: data.ok ? data.order : null },
        }));

        // Remove terminal orders from localStorage
        if (data.ok && data.order?.onDemand) {
          const status = data.order.onDemand.status;
          if (status === "order_complete" || status === "cancelled") {
            removeLocalOrder(orderId);
            this.setState((prev) => ({
              localOrders: prev.localOrders.filter((o) => String(o.orderId) !== String(orderId)),
            }));
          }
        }
      })
      .catch(() => {
        this.setState((prev) => ({
          loadingOrders: { ...prev.loadingOrders, [orderId]: false },
        }));
      });
  }

  handleLookup = () => {
    const { lookupOrderId, lookupOrderDate } = this.state;
    if (!lookupOrderId || !lookupOrderDate) return;

    this.setState({ lookupLoading: true, lookupError: null, lookupSuccess: null });

    fetch(
      `${basePath}/api/v1/on-demand-orders/public/lookup?orderId=${encodeURIComponent(lookupOrderId)}&orderDate=${encodeURIComponent(lookupOrderDate)}`
    )
      .then((r) => r.json())
      .then((data) => {
        if (!data.ok || !data.order) {
          this.setState({ lookupLoading: false, lookupError: "לא נמצאה הזמנה עם הפרטים שסופקו" });
          return;
        }

        const order = data.order;
        const orderId = String(order.orderId);
        const token = order.onDemand?.customerAccessToken;
        const status = order.onDemand?.status;
        const isTerminal = STATUS_MAP[status]?.terminal === true;

        if (isTerminal) {
          this.setState((prev) => ({
            lookupLoading: false,
            lookupSuccess: `הזמנה מספר ${orderId} נמצאה, אך היא כבר אינה פעילה`,
            serverOrders: { ...prev.serverOrders, [orderId]: order },
          }));
          return;
        }

        // Persist to localStorage so this device remembers the order
        const existing = getLocalOrders();
        const updated = [
          {
            orderId,
            customerAccessToken: token,
            orderDateKey: order.onDemand?.orderDateKey,
            createdAt: order.createdAt,
            status,
          },
          ...existing.filter((e) => e.orderId !== orderId),
        ];
        saveLocalOrders(updated);

        this.setState((prev) => ({
          lookupLoading: false,
          lookupSuccess: `הזמנה מספר ${orderId} נמצאה בהצלחה`,
          localOrders: updated,
          serverOrders: { ...prev.serverOrders, [orderId]: order },
        }));
      })
      .catch(() => {
        this.setState({ lookupLoading: false, lookupError: "שגיאת חיבור, אנא נסה שנית" });
      });
  };

  openRemoveConfirm = (orderId) => {
    this.setState({ removeConfirmOrderId: String(orderId) });
  };

  closeRemoveConfirm = () => {
    this.setState({ removeConfirmOrderId: null });
  };

  handleConfirmRemoveOrder = () => {
    const { removeConfirmOrderId } = this.state;
    if (!removeConfirmOrderId) return;

    const orderId = String(removeConfirmOrderId);
    removeLocalOrder(orderId);
    this.setState((prev) => ({
      localOrders: prev.localOrders.filter((o) => String(o.orderId) !== String(orderId)),
      removeConfirmOrderId: null,
    }));
  };

  renderTimeline(currentStatus) {
    const { classes } = this.props;
    const currentIndex = TIMELINE_STATUSES.findIndex((s) => s.key === currentStatus);

    return (
      <div style={{ direction: "rtl", margin: "20px 0", paddingRight: 16 }}>
        {TIMELINE_STATUSES.map((step, index) => {
          const isDone = index < currentIndex;
          const isCurrent = index === currentIndex;
          const isFuture = index > currentIndex;
          const color = isDone || isCurrent ? "#16a34a" : "#d1d5db";
          const isLast = index === TIMELINE_STATUSES.length - 1;

          return (
            <div
              key={step.key}
              style={{
                display: "flex",
                alignItems: "flex-start",
                marginBottom: 12,
                position: "relative",
                width: "100%",
              }}
            >
              {!isLast && (
                <div
                  style={{
                    position: "absolute",
                    top: 22,
                    right: 10,
                    width: 2,
                    height: "calc(100% + 2px)",
                    background: "#e5e7eb",
                    zIndex: 0,
                  }}
                />
              )}
              <div style={{ marginLeft: 12, marginTop: 2, flexShrink: 0, zIndex: 1 }}>
                {isDone || isCurrent ? (
                  <span
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      backgroundColor: "#16a34a",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CheckIcon style={{ color: "#ffffff", fontSize: 16 }} />
                  </span>
                ) : (
                  <RadioButtonUncheckedIcon style={{ color, fontSize: 22 }} />
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0, textAlign: "right" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Typography
                    variant="body2"
                    style={{
                      fontWeight: isCurrent ? 700 : 400,
                      color: isFuture ? "#9ca3af" : "#1f2937",
                    }}
                  >
                    {step.title}
                  </Typography>
                  {isCurrent && (
                    <Chip
                      label="שלב נוכחי"
                      size="small"
                      className={classes.chip}
                      style={{ backgroundColor: "#16a34a", color: "white" }}
                    />
                  )}
                </div>
                {isCurrent && (
                  <Typography variant="caption" style={{ color: "#4b5563" }}>
                    {step.explanation}
                  </Typography>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  renderOrderCard(localOrder, serverOrder, isLoading) {
    const { classes } = this.props;
    const orderId = String(localOrder.orderId);
    const onDemand = serverOrder?.onDemand || {};
    const status = onDemand.status || localOrder.status || "received";
    const statusEntry = STATUS_MAP[status];
    const isTerminal = statusEntry?.terminal || false;
    const isCancelled = status === "cancelled";

    const createdDate = localOrder.createdAt
      ? new Date(localOrder.createdAt).toLocaleDateString("he-IL")
      : "-";

    return (
      <Paper key={orderId} className={classes.orderCard} elevation={2}>
        <div className={classes.orderTitle}>
          <div className={classes.orderTitleMain}>
            <DirectionsBoatIcon style={{ color: "#2563eb" }} />
            <Typography variant="h6" className={classes.orderTitleText}>
              הזמנה מספר {orderId}
            </Typography>
            <Chip
              label={statusEntry ? statusEntry.title : status}
              size="small"
              style={{ backgroundColor: statusColor(status), color: "white", fontWeight: 600 }}
            />
          </div>
          <div className={classes.orderTitleMeta}>
            <Typography variant="caption" style={{ color: "#6b7280" }}>
              {createdDate}
            </Typography>
            <Tooltip title="הסר הזמנה מהרשימה">
              <Button
                size="small"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => this.openRemoveConfirm(orderId)}
                style={{ color: "#9ca3af", minWidth: "auto" }}
              >
                הסר
              </Button>
            </Tooltip>
          </div>
        </div>

        {isLoading && (
          <div style={{ textAlign: "center", padding: 20 }}>
            <CircularProgress size={28} />
          </div>
        )}

        {!isLoading && serverOrder && (
          <>
            {/* Items */}
            {(onDemand.items || []).length > 0 && (
              <div className={classes.itemsList}>
                {onDemand.items.map((item, i) => {
                  const label = `${item.displayName || item.ean} ${item.displaySize || ""} ×${item.quantity}`;
                  const imageUrl = item.imageUrl || (item.ean ? `${basePath}/api/v1/media/product/${item.ean}` : "");

                  return (
                    <div key={i} className={classes.orderItem}>
                      {imageUrl && (
                        <img
                          src={imageUrl}
                          alt={item.displayName || item.ean}
                          className={classes.orderItemImage}
                        />
                      )}
                      <Typography variant="caption" style={{ fontSize: 12 }}>
                        {label}
                      </Typography>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Timeline or terminal message */}
            {isTerminal ? (
              <div style={{ padding: "16px 0", color: isCancelled ? "#dc2626" : "#16a34a" }}>
                <Typography variant="body2">{statusEntry?.explanation}</Typography>
              </div>
            ) : (
              this.renderTimeline(status)
            )}

            {/* Update history */}
            {(onDemand.updates || []).length > 0 && (
              <div className={classes.updateHistory} dir="rtl">
                <Typography variant="subtitle2" style={{ width: "100%", marginBottom: 8, fontSize: 14, textAlign: "right" }}>
                  היסטוריית עדכונים
                </Typography>
                {[...(onDemand.updates || [])].reverse().map((u, i) => (
                  <div key={i} className={classes.updateEntry}>
                    <Typography variant="body2" style={{ width: "100%", fontSize: 15, fontWeight: 700, textAlign: "right" }}>
                      {u.title || u.status}
                    </Typography>
                    {u.customMessage && (
                      <Typography variant="body2" style={{ width: "100%", color: "#d97706", fontSize: 13, marginTop: 3, textAlign: "right" }}>
                        {u.customMessage}
                      </Typography>
                    )}
                    <Typography variant="caption" dir="rtl" style={{ width: "100%", color: "#4b5563", fontSize: 12, fontWeight: 600, textAlign: "right" }}>
                      {u.createdAt
                        ? `${new Date(u.createdAt).toLocaleString("he-IL")} (${formatTimeSince(u.createdAt)})`
                        : ""}
                    </Typography>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {!isLoading && !serverOrder && (
          <Typography variant="body2" style={{ color: "#9ca3af", marginTop: 8 }}>
            לא ניתן לטעון פרטי הזמנה כרגע
          </Typography>
        )}
      </Paper>
    );
  }

  renderLookupPanel() {
    const { classes } = this.props;
    const { lookupOrderId, lookupOrderDate, lookupLoading, lookupError, lookupSuccess } = this.state;

    return (
      <Paper className={classes.lookupPanel} elevation={0} dir="rtl" style={{ direction: "rtl", textAlign: "right" }}>
        <Typography variant="h6" style={{ marginBottom: 4, textAlign: "right" }}>
          מחפשים הזמנה ממכשיר אחר?
        </Typography>
        <Typography variant="body2" style={{ color: "#6b7280", textAlign: "right" }}>
          הזינו את מספר ההזמנה ותאריך ביצוע ההזמנה כדי לאחזר את הסטטוס
        </Typography>

        <div
          className={classes.lookupRow}
          dir="rtl"
          style={{ direction: "rtl", justifyContent: "flex-start", textAlign: "right" }}
        >
          <TextField
            label="מספר הזמנה"
            value={lookupOrderId}
            onChange={(e) => this.setState({ lookupOrderId: e.target.value })}
            variant="outlined"
            size="small"
            className={`${classes.lookupField} ${classes.lookupFieldId}`}
            type="number"
            inputProps={{ style: { direction: "rtl", textAlign: "right" } }}
            InputLabelProps={{ style: { direction: "rtl", right: 14, left: "auto", transformOrigin: "top right" } }}
          />
          <TextField
            label="תאריך הזמנה"
            value={lookupOrderDate}
            onChange={(e) => this.setState({ lookupOrderDate: e.target.value })}
            variant="outlined"
            size="small"
            placeholder="2025-06-01"
            className={`${classes.lookupField} ${classes.lookupFieldDate}`}
            inputProps={{ style: { direction: "rtl", textAlign: "right" } }}
            InputLabelProps={{ style: { direction: "rtl", right: 14, left: "auto", transformOrigin: "top right" } }}
          />
          <Button
            variant="contained"
            color="primary"
            className={classes.lookupButton}
            onClick={this.handleLookup}
            disabled={lookupLoading || !lookupOrderId || !lookupOrderDate}
            startIcon={lookupLoading ? <CircularProgress size={16} /> : <SearchIcon />}
          >
            {lookupLoading ? "מחפש..." : "חפש הזמנה"}
          </Button>
        </div>

        {lookupError && (
          <Typography variant="body2" style={{ color: "#dc2626", marginTop: 8, textAlign: "right" }}>
            {lookupError}
          </Typography>
        )}
        {lookupSuccess && (
          <Typography variant="body2" style={{ color: "#16a34a", marginTop: 8, textAlign: "right" }}>
            ✓ {lookupSuccess}
          </Typography>
        )}
      </Paper>
    );
  }

  render() {
    const { classes } = this.props;
    const { localOrders, serverOrders, loadingOrders, removeConfirmOrderId } = this.state;

    return (
      <div className={classes.root}>
        <div className={classes.header}>
          <DirectionsBoatIcon style={{ fontSize: 32, color: "#2563eb" }} />
          <div>
            <Typography variant="h4" className={classes.headerTitle}>
              הזמנות מראש שלי
            </Typography>
            <Typography variant="body2" style={{ color: "#6b7280" }}>
              מעקב אחר הזמנות מהמלאי האירופי
            </Typography>
          </div>
        </div>

        {localOrders.map((lo) =>
          this.renderOrderCard(lo, serverOrders[lo.orderId], loadingOrders[lo.orderId])
        )}

        {localOrders.length === 0 && (
          <div className={classes.emptyState}>
            <DirectionsBoatIcon style={{ fontSize: 56, color: "#d1d5db", display: "block", margin: "0 auto 16px" }} />
            <Typography variant="h6" style={{ color: "#9ca3af" }}>
              אין הזמנות מראש שמורות במכשיר זה
            </Typography>
            <Typography variant="body2" style={{ color: "#9ca3af", marginTop: 8 }}>
              הזמנות מהמלאי האירופי יופיעו כאן לאחר ביצוע ההזמנה
            </Typography>
          </div>
        )}

        {this.renderLookupPanel()}

        <Dialog
          open={Boolean(removeConfirmOrderId)}
          onClose={this.closeRemoveConfirm}
          PaperProps={{ style: { direction: "rtl", textAlign: "right" } }}
        >
          <DialogTitle disableTypography>
            <Typography variant="h6" style={{ textAlign: "right" }}>
              להסיר הזמנה מהרשימה?
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" style={{ color: "#4b5563", textAlign: "right" }}>
              הפעולה תסיר את הזמנה מספר {removeConfirmOrderId} מהמכשיר הזה בלבד. ההזמנה לא תימחק מהמערכת,
              ותוכלו להוסיף אותה שוב באמצעות מספר ההזמנה ותאריך ההזמנה.
            </Typography>
          </DialogContent>
          <DialogActions style={{ justifyContent: "flex-start" }}>
            <Button onClick={this.closeRemoveConfirm} color="primary">
              ביטול
            </Button>
            <Button onClick={this.handleConfirmRemoveOrder} style={{ color: "#dc2626" }}>
              הסר מהרשימה
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

export default withStyles(styles)(PendingOrdersPage);
